import { BpSelect } from '../../../../shared/components/forms/select/BpSelect'
import { IOption } from '../../../../shared/components/forms/search-input/BpSearchInput'
import { toTextOptions } from '../../../../shared/lib/helpers/ToTextOptions'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import { Controller, useForm } from 'react-hook-form'
import React, { useEffect, useMemo } from 'react'
import { FilterDrop } from '../../../../shared/components/cards/filter-drop/FilterDrop'
import moment from 'moment/moment'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_RANGE_FORMAT,
} from '../../../../shared/consts/DefaultRangeFormat.const'
import {
  selectAdvertiserReportFilters,
  selectAdvertiserReportTabCount,
  selectAllowDecloak,
  selectCountry,
  selectProject,
  setAdvertiserReportFilter,
  setCountry,
  setProject,
} from '../../model/paidResultsSlice'
import { AdvertiserReportFilterInterface } from '../../model/types/AdvertiserReportFilter.interface'
import { Switch } from 'antd'
import {
  IWithSearchParamsProps,
  withSearchParams,
} from '../../../../shared/lib/hoc/WithSearchParams'
import { Trans, useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { BpTabs } from '../../../../shared/components/utility/tabs/BpTabs'
import { BpTab } from '../../../../shared/components/utility/tabs/tab/BpTab'
import { PaidResultsPanel } from '../paid-results-panel/PaidResultsPanel'
import { AddProjectButton } from '../../../../shared/components/buttons/add-project-button/AddProjectButton'
import { EditCountriesButton } from '../../../../shared/components/buttons/edit-countries-button/EditCountriesButton'
import { selectNotifications } from '../../../../app/store/reducers/AuthSlice'
import { FilterChips } from '../../../../shared/components/utility/filter-chips/FilterChips'
import { Notify } from '../../../../shared/components/text/notify/Notify'
import { DateSelect } from '../../../../shared/components/forms/date-select/DateSelect'
import clsx from 'clsx'
import styles from './PaidResultsFilters.module.scss'
import {
  GetFilterAdvertiserDomainsApiArg,
  GetFilterAffiliatesApiArg,
  GetFilterKeywordsApiArg,
  useGetDomainTagListQuery,
  useGetFilterAdvertiserDomainsQuery,
  useGetFilterAdvertiserNamesQuery,
  useGetFilterAffiliatesQuery,
  useGetFilterBrowserQuery,
  useGetFilterCountryQuery,
  useGetFilterEngineQuery,
  useGetFilterKeywordsQuery,
  useGetFilterProjectsQuery,
} from '../../../../app/api'
import { useSearchState } from '../../../../lib/hooks/useSearchState'
import { useReversedFilters } from '../../../../lib/hooks/useReversedFilters'
import {
  resetFilterFields,
  useRegisterAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { ProjectIcon } from '../../../../shared/components/icons/filters/ProjectIcon'
import { CountryIcon } from '../../../../shared/components/icons/filters/CountryIcon'
import { SearchEngineIcon } from '../../../../shared/components/icons/filters/SearchEngineIcon'
import { BrowserIcon } from '../../../../shared/components/icons/filters/BrowserIcon'
import { TagIcon } from '../../../../shared/components/icons/filters/TagIcon'
import { KeywordIcon } from '../../../../shared/components/icons/filters/KeywordIcon'
import { AffiliateIcon } from '../../../../shared/components/icons/filters/AffiliateIcon'
import { AdvertiserIcon } from '../../../../shared/components/icons/filters/AdvertiserIcon'
import { DomainIcon } from '../../../../shared/components/icons/filters/DomainIcon'
import { withSelectedOptions } from '../../../../shared/lib/helpers/WithSelectedOptions'
import { renderCountryOption } from '../../../../shared/components/utility/country-option/CountryOption'
import { restoreAppliedValues } from '../../../../shared/lib/helpers/AppliedValues'

const REVERSED_FILTER_KEYS: Record<string, string> = {
  engine: 'engine_id',
  browser: 'browser_id',
  keyword: 'keyword_id',
  affiliate: 'affiliate_id',
  advertiserName: 'advertiser_id',
  tags: 'tag_ids',
}

/** Фильтры, которые сбрасывает Clear filters, и их значения по умолчанию. */
const CLEARABLE_FILTERS: Partial<AdvertiserReportFilterInterface> = {
  country: '',
  advertiser: '',
  engine: undefined,
  browser: '',
  keyword: '',
  affiliate: '',
  advertiserName: '',
  show_hidden: false,
  is_decloak: false,
  tags: '',
}

const Filter = ({
  searchParams,
  setSearchParams,
}: IWithSearchParamsProps<AdvertiserReportFilterInterface>) => {
  const { t } = useTranslation()
  const tabCount = useAppSelector(selectAdvertiserReportTabCount)
  const allowDecloak = useAppSelector<boolean>(selectAllowDecloak)
  const notifications = useAppSelector(selectNotifications)
  const filters = useAppSelector<AdvertiserReportFilterInterface>(selectAdvertiserReportFilters)
  const {
    country,
    project,
    dateRange,
    advertiser,
    policy,
    keyword,
    affiliate,
    advertiserName,
    show_hidden,
    engine,
    browser,
    is_decloak,
    tags,
    reversed_filters,
  } = filters
  const { is_demo: isDemo, has_paid_links: hasPaidLinks } = useAppSelector(selectProject)
  const currentCountry = useAppSelector(selectCountry)
  const dispatch = useAppDispatch()

  const [search, setSearch] = useSearchState({
    keyword: '',
    affiliate: '',
    advertiserName: '',
    advertiser: '',
  })

  const showTrackingNotification = useMemo(() => {
    return (
      policy === 'non_compliant_affiliates' &&
      notifications?.some(
        (value) =>
          String(value.project_id) === project && value.type === 'affiliate_tracking_disabled',
      )
    )
  }, [notifications, project])

  const {
    getValues,
    setValue,
    control,
    formState: { defaultValues },
  } = useForm<AdvertiserReportFilterInterface>({
    mode: 'onChange',
    defaultValues: {
      country,
      project,
      dateRange,
      advertiser,
      policy,
      keyword,
      affiliate,
      advertiserName,
      show_hidden,
      engine,
      browser,
      is_decloak,
      tags,
      ...searchParams,
    },
  })

  useEffect(() => {
    applyFilter()
  }, [])

  const {
    data: projects,
    isFetching: projectsFetching,
    isLoading: projectsLoading,
  } = useGetFilterProjectsQuery({})

  const {
    data: countries,
    isFetching: countriesFetching,
    isLoading: countriesLoading,
  } = useGetFilterCountryQuery(
    { projectId: project ? +project : undefined, withKeywordsState: true },
    { skip: !project },
  )

  const {
    data: advertisers,
    isFetching: advertisersFetching,
    isLoading: advertisersLoading,
  } = useGetFilterAdvertiserDomainsQuery(
    {
      projectId: project ? +project : undefined,
      search: search.advertiser,
      date: dateRange,
      seo: false,
    } as GetFilterAdvertiserDomainsApiArg,
    { skip: !project },
  )

  const {
    data: keywords,
    isFetching: keywordsFetching,
    isLoading: keywordsLoading,
  } = useGetFilterKeywordsQuery(
    {
      projectId: project,
      search: search.keyword,
    } as unknown as GetFilterKeywordsApiArg,
    { skip: !project },
  )

  const {
    data: affiliates,
    isFetching: affiliatesFetching,
    isLoading: affiliatesLoading,
  } = useGetFilterAffiliatesQuery(
    {
      date: dateRange,
      isSeo: false,
      projectId: +project,
      search: search.affiliate,
    } as GetFilterAffiliatesApiArg,
    { skip: !project },
  )

  const {
    data: advertiserNames,
    isFetching: advertiserNamesFetching,
    isLoading: advertiserNamesLoading,
  } = useGetFilterAdvertiserNamesQuery(
    {
      date: dateRange,
      projectId: +project,
      search: search.advertiserName,
    },
    { skip: !project },
  )

  const {
    data: tagOptions,
    isFetching: tagOptionsFetching,
    isLoading: tagOptionsLoading,
  } = useGetDomainTagListQuery({ projectId: Number(project) }, { skip: !project })

  const {
    data: engines,
    isFetching: enginesFetching,
    isLoading: enginesLoading,
  } = useGetFilterEngineQuery({ projectId: project ? +project : undefined }, { skip: !project })

  const {
    data: browsers,
    isFetching: browsersFetching,
    isLoading: browsersLoading,
  } = useGetFilterBrowserQuery()

  useEffect(() => {
    if (projects && !projects.length) {
      setValue('project', '')
      applyFilter()
    }
  }, [projects])

  const policies = [
    {
      text: t('fields.options.All'),
      value: '',
    },
    {
      text: t('fields.options.Affiliate sites'),
      value: 'non_compliant_affiliates',
    },
    {
      text: t('fields.options.TM abuse'),
      value: 'trademark_abuse',
    },
    { text: t('fields.options.Coupon sites'), value: 'coupon_sites' },
    {
      text: t('fields.options.Lookalike domains'),
      value: 'trademark_abuse_url',
    },
  ]

  const notify = isDemo
    ? 'demo-project'
    : showTrackingNotification
      ? 'tracking-disabled'
      : currentCountry?.keywords_state === 'deleted'
        ? 'missing-keywords'
        : undefined

  const applyFilterWithReversed = (reversed: string[]) => {
    const values = { ...getValues(), reversed_filters: reversed.join(',') }
    dispatch(setAdvertiserReportFilter(values))
    if (setSearchParams) setSearchParams(values)
  }

  const {
    reversedFilters,
    isReversed,
    isReversedApplied,
    toggleReversedFilter,
    toggleReversedFilterAndApply,
    clearReversedFilter,
    resetReversedFilters,
    restoreReversedFilters,
  } = useReversedFilters({
    keys: REVERSED_FILTER_KEYS,
    applied: reversed_filters,
    fromUrl: searchParams?.reversed_filters,
    onApply: applyFilterWithReversed,
  })

  const applyFilter = () => applyFilterWithReversed(reversedFilters)

  useRegisterAppliedFilters({
    values: filters,
    defaults: CLEARABLE_FILTERS,
    onClear: () => {
      resetFilterFields(setValue, CLEARABLE_FILTERS)
      dispatch(setCountry(undefined))
      resetReversedFilters()
      applyFilterWithReversed([])
    },
  })

  return (
    <section className={clsx(styles.section)}>
      <form className={clsx(styles.form)}>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, ...fieldProps } }) => {
              const defaultValue =
                defaultValues?.project ??
                project ??
                projects?.find((option) => !option.is_demo)?.value ??
                projects?.[0]?.value

              return (
                <BpSelect
                  {...fieldProps}
                  Icon={ProjectIcon}
                  bottomSection={<AddProjectButton />}
                  defaultValue={defaultValue}
                  disabled={projectsLoading || projectsFetching}
                  fieldSize='lg'
                  loading={projectsLoading || projectsFetching}
                  options={projects}
                  title={t('fields.project')}
                  onChange={(e) => {
                    onChange(e)

                    const option = projects?.find((option) => option.value === e.target.value)

                    if (option) {
                      dispatch(setProject(option))
                    }

                    if (e.target.value !== defaultValue) {
                      setValue('country', '')
                      setValue('is_decloak', false)
                    }
                    applyFilter()
                  }}
                ></BpSelect>
              )
            }}
            control={control}
            name='project'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={CountryIcon}
                bottomSection={<EditCountriesButton project={{ value: project, isDemo }} />}
                disabled={countriesLoading || countriesFetching}
                fieldSize='lg'
                loading={countriesLoading || countriesFetching}
                optionRender={renderCountryOption}
                options={countries}
                title={t('fields.country')}
                value={value?.split(',')}
                onChange={(e) => {
                  const selected = [].slice
                    .call(e.target.selectedOptions)
                    .map((o: IOption) => o.value)

                  onChange(selected.join(','))

                  const option =
                    selected.length === 1
                      ? countries?.find((option) => option.value === selected[0])
                      : undefined
                  dispatch(setCountry(option))
                }}
                onDropClose={applyFilter}
              ></BpSelect>
            )}
            control={control}
            name='country'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { value, onChange, ...fieldProps } }) => (
              <BpSelect
                {...fieldProps}
                multiple
                Icon={DomainIcon}
                enableChipLoad={false}
                fieldSize='lg'
                loading={advertisersLoading || advertisersFetching}
                options={toTextOptions(advertisers, value)}
                placeholder={t('fields.domain')}
                value={value?.split(',')}
                onChange={(e) => {
                  onChange(
                    [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)
                      .join(','),
                  )
                }}
                onDropClose={applyFilter}
                onSearch={setSearch('advertiser')}
              ></BpSelect>
            )}
            control={control}
            name='advertiser'
          />
        </div>
        <div className={clsx(styles.field)}>
          <Controller
            render={({ field: { onChange, value } }) => {
              return (
                <DateSelect
                  value={[moment(value, DEFAULT_DATE_FORMAT), moment(value, DEFAULT_RANGE_FORMAT)]}
                  onChange={(_, value) => {
                    onChange(`${value[0]} - ${value[1]}`)
                    applyFilter()
                  }}
                />
              )
            }}
            control={control}
            name='dateRange'
          />
        </div>
        <div className={clsx(styles.filterDropWrapper)}>
          <FilterDrop
            values={getValues([
              'engine',
              'browser',
              'keyword',
              'affiliate',
              'advertiserName',
              'show_hidden',
              'is_decloak',
              'tags',
            ])}
            onClear={() => {
              setValue('keyword', '')
              setValue('engine', '')
              setValue('browser', '')
              setValue('affiliate', '')
              setValue('show_hidden', false)
              setValue('tags', '')
              setValue('advertiserName', '')
              resetReversedFilters()
            }}
            onHide={() => {
              restoreAppliedValues(getValues, setValue, filters)
              restoreReversedFilters()
            }}
            onSubmit={applyFilter}
          >
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('engine')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('engine')}
                    />
                  }
                  Icon={SearchEngineIcon}
                  disabled={enginesLoading || enginesFetching}
                  label={t('fields.searchEngine')}
                  loading={enginesLoading || enginesFetching}
                  options={engines}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='engine'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('browser')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('browser')}
                    />
                  }
                  Icon={BrowserIcon}
                  disabled={browsersLoading || browsersFetching}
                  label={t('fields.browser')}
                  loading={browsersLoading || browsersFetching}
                  options={browsers}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                />
              )}
              control={control}
              name='browser'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('keyword')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('keyword')}
                    />
                  }
                  Icon={KeywordIcon}
                  enableChipLoad={false}
                  label={t('fields.keyword')}
                  loading={keywordsLoading || keywordsFetching}
                  options={withSelectedOptions('keyword', keywords, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('keyword')}
                ></BpSelect>
              )}
              control={control}
              name='keyword'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('affiliate')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('affiliate')}
                    />
                  }
                  Icon={AffiliateIcon}
                  enableChipLoad={false}
                  label={t('fields.affiliate')}
                  loading={affiliatesLoading || affiliatesFetching}
                  options={withSelectedOptions('affiliate', affiliates, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('affiliate')}
                ></BpSelect>
              )}
              control={control}
              name='affiliate'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('advertiserName')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('advertiserName')}
                    />
                  }
                  Icon={AdvertiserIcon}
                  enableChipLoad={false}
                  label={t('fields.advertiser')}
                  loading={advertiserNamesLoading || advertiserNamesFetching}
                  options={withSelectedOptions('advertiser', advertiserNames, value)}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    onChange(
                      [].slice
                        .call(e.target.selectedOptions)
                        .map((o: IOption) => o.value)
                        .join(','),
                    )
                  }}
                  onSearch={setSearch('advertiserName')}
                ></BpSelect>
              )}
              control={control}
              name='advertiserName'
            />
            <Controller
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <BpSelect
                  {...fieldProps}
                  multiple
                  action={
                    <BpCheckbox
                      checked={isReversed('tags')}
                      label={t('fields.exclude')}
                      theme={'grey'}
                      onChange={() => toggleReversedFilter('tags')}
                    />
                  }
                  options={tagOptions?.data?.items?.map((tag: any) => ({
                    text: tag.name,
                    value: String(tag.id),
                  }))}
                  Icon={TagIcon}
                  disabled={tagOptionsFetching || tagOptionsLoading}
                  enableChipLoad={false}
                  label={t('fields.tag')}
                  loading={tagOptionsFetching || tagOptionsLoading}
                  placeholder={t('fields.other.selectFromList')}
                  value={value?.split(',')}
                  onChange={(e) => {
                    const value = [].slice
                      .call(e.target.selectedOptions)
                      .map((o: IOption) => o.value)

                    onChange(value.join(','))
                  }}
                ></BpSelect>
              )}
              control={control}
              name='tags'
            />
            <Controller
              render={({ field }) => (
                <div className={clsx(styles.switchRow)}>
                  <span className={clsx(styles.switchLabel)}>{t('fields.showHiddenSites')}</span>
                  <Switch {...field} checked={field.value} defaultChecked={show_hidden}></Switch>
                </div>
              )}
              control={control}
              name='show_hidden'
            />
            {allowDecloak && (
              <Controller
                render={({ field }) => (
                  <div className={clsx(styles.switchRow)}>
                    <span className={clsx(styles.switchLabel)}>
                      {t('fields.showBlackhatSites')}
                    </span>
                    <Switch {...field} checked={field.value} defaultChecked={is_decloak}></Switch>
                  </div>
                )}
                control={control}
                name='is_decloak'
              />
            )}
          </FilterDrop>
        </div>
      </form>
      <div className={clsx(styles.chipsWrapper)}>
        <FilterChips
          applyCallBack={() => {
            resetReversedFilters()
            applyFilterWithReversed([])
          }}
          clearCallBack={(field: any) => {
            setValue(field, '')
            applyFilterWithReversed(clearReversedFilter(field))
          }}
          items={[
            {
              title: t('fields.searchEngine'),
              field: 'engine',
              reversed: isReversedApplied('engine'),
              value: engine,
              data: engines,
              type: 'multiselect',
              reversible: true,
            },
            {
              title: t('fields.browser'),
              field: 'browser',
              reversed: isReversedApplied('browser'),
              value: browser,
              data: browsers,
              type: 'multiselect',
              reversible: true,
            },
            {
              title: t('fields.keyword'),
              field: 'keyword',
              reversed: isReversedApplied('keyword'),
              value: keyword,
              reversible: true,
              data: withSelectedOptions('keyword', keywords, keyword),
              type: 'multiselect',
            },
            {
              title: t('fields.affiliate'),
              field: 'affiliate',
              reversed: isReversedApplied('affiliate'),
              value: affiliate,
              reversible: true,
              data: withSelectedOptions('affiliate', affiliates, affiliate),
              type: 'multiselect',
            },
            {
              title: t('fields.advertiser'),
              field: 'advertiserName',
              reversed: isReversedApplied('advertiserName'),
              value: advertiserName,
              reversible: true,
              data: withSelectedOptions('advertiser', advertiserNames, advertiserName),
              type: 'multiselect',
            },
            {
              title: t('fields.tag'),
              field: 'tags',
              reversed: isReversedApplied('tags'),
              value: tags,
              data: tagOptions?.data?.items?.map((tag: any) => ({
                text: tag.name,
                value: String(tag.id),
              })),
              type: 'multiselect',
              reversible: true,
            },
          ]}
          reverseCallBack={toggleReversedFilterAndApply}
        />
      </div>
      {notify && (
        <Notify className={'mt-[20px] -mb-[16px]'}>
          {
            {
              'demo-project': (
                <Trans i18nKey='other.demoProjectSelected'>
                  <NavLink className={clsx(styles.demoBannerLink)} to='/projects' />
                </Trans>
              ),
              'tracking-disabled': (
                <>
                  <Trans i18nKey='other.trackingDisabled'>
                    <NavLink
                      className={clsx(styles.demoBannerLink)}
                      to={project ? `/projects/${project}/affiliate-tracking` : '/projects'}
                    />
                  </Trans>
                </>
              ),
              'missing-keywords': (
                <Trans i18nKey='other.missingKeywords'>
                  <NavLink
                    className={clsx(styles.demoBannerLink)}
                    to={`/projects/${project}/keywords`}
                  />
                </Trans>
              ),
            }[notify]
          }
        </Notify>
      )}
      {hasPaidLinks && (
        <div className={clsx(styles.tabsWrapper)}>
          <BpTabs
            active={policies.find((value) => value.value === searchParams?.policy)?.text}
            size={'md'}
            onToggle={(e) => {
              const value = policies.find((value) => value.text === e)?.value

              if (e !== policy) {
                setValue('policy', value)
                applyFilter()
              }
            }}
          >
            {policies.map(({ text, value }) => (
              <BpTab
                key={value}
                counter={Number(tabCount?.[value]) ? tabCount?.[value] : undefined}
                panel={<PaidResultsPanel />}
                title={text}
              />
            ))}
          </BpTabs>
        </div>
      )}
    </section>
  )
}

export const PaidResultsFilters = withSearchParams<
  IWithSearchParamsProps<AdvertiserReportFilterInterface>,
  AdvertiserReportFilterInterface
>(Filter, selectAdvertiserReportFilters)
